import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ComplianceControl, Dpia, HealthScore, Incident, Risk } from '@hospasense/contracts';
import {
  downloadComplianceExport,
  fetchComplianceControls,
  fetchDpias,
  fetchHealthScores,
  fetchIncidents,
  fetchReports,
  fetchRisks,
  type ReportListItem,
} from '../../lib/queries-s5.js';
import { Badge, Button, PageHeader, StatusPill, useToast } from '../../components/ui/index.js';
import { HomeSection, QuickLinks, StatCard } from './home-shared.js';

const BAND_TONE: Record<string, 'success' | 'warning' | 'error'> = {
  healthy: 'success',
  watch: 'warning',
  at_risk: 'error',
};

/** Customer Success portfolio — worst-first, with the story behind each score. */
export function CustomerSuccessPortfolio({ displayName }: { displayName: string }) {
  const [scores, setScores] = useState<HealthScore[]>([]);

  useEffect(() => {
    void fetchHealthScores()
      .then(setScores)
      .catch(() => setScores([]));
  }, []);

  const atRisk = scores.filter((s) => (s.reviewBand ?? s.band) === 'at_risk').length;
  const unreviewed = scores.filter((s) => !s.reviewBand).length;

  return (
    <div className="space-y-6" data-testid="home-customer-success">
      <PageHeader
        title="Customer Success Portfolio"
        subtitle={`Hello ${displayName}. Your portfolio, worst-first. Scores are advisory — the follow-up is always yours.`}
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard
          label="Portfolio size"
          value={scores.length}
          hint="scored organisations"
          to="/health"
        />
        <StatCard
          label="At risk"
          value={atRisk}
          tone={atRisk > 0 ? 'error' : 'success'}
          to="/health"
        />
        <StatCard label="Awaiting your review" value={unreviewed} to="/health" />
      </div>
      <HomeSection title="Worst-first">
        <ul className="space-y-2">
          {scores.slice(0, 5).map((s) => (
            <li
              key={s.organisationId}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface px-4 py-2 text-sm"
            >
              <span className="w-10 text-lg font-bold tabular-nums">{s.score ?? '—'}</span>
              <span className="min-w-0 flex-1 font-medium text-text-primary">
                {s.organisationName ?? s.organisationId}
              </span>
              {s.band && <StatusPill tone={BAND_TONE[s.band]!} label={s.band} />}
              {s.reviewBand && <Badge tone={BAND_TONE[s.reviewBand]!}>reviewed</Badge>}
              <Link to="/health" className="text-xs font-medium text-link hover:underline">
                factors →
              </Link>
            </li>
          ))}
          {scores.length === 0 && (
            <li className="text-sm text-text-muted">No health snapshots yet.</li>
          )}
        </ul>
      </HomeSection>
      <QuickLinks
        links={[
          { to: '/health', label: 'Customer health' },
          { to: '/organisations', label: 'Organisations' },
          { to: '/onboarding', label: 'Onboarding' },
          { to: '/support', label: 'Support queue' },
        ]}
      />
    </div>
  );
}

/** Security & Privacy cockpit — controls, risks, incidents, DPIAs. */
export function SecurityPrivacyCockpit({ displayName }: { displayName: string }) {
  const [controls, setControls] = useState<ComplianceControl[]>([]);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [dpias, setDpias] = useState<Dpia[]>([]);

  useEffect(() => {
    void Promise.allSettled([
      fetchComplianceControls().then(setControls),
      fetchRisks().then(setRisks),
      fetchIncidents().then(setIncidents),
      fetchDpias().then(setDpias),
    ]);
  }, []);

  const unevidenced = controls.filter((c) => !c.evidenced).length;
  const untested = controls.filter((c) => !c.tested).length;
  const highRisks = risks.filter((r) => r.rating >= 6 && r.status !== 'closed').length;
  const openIncidents = incidents.filter((i) => i.status !== 'closed').length;
  const dpiasInReview = dpias.filter((d) => d.status === 'in_review').length;

  return (
    <div className="space-y-6" data-testid="home-security">
      <PageHeader
        title="Security & Privacy Cockpit"
        subtitle={`Hello ${displayName}. Control posture, risk pressure and privacy workload — four independent flags, never one green checkbox.`}
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard
          label="Controls lacking evidence"
          value={unevidenced}
          tone={unevidenced > 0 ? 'warning' : 'success'}
          to="/compliance"
        />
        <StatCard
          label="Controls untested in window"
          value={untested}
          tone={untested > 0 ? 'warning' : 'success'}
          to="/compliance"
        />
        <StatCard
          label="High risks (≥6/9)"
          value={highRisks}
          tone={highRisks > 0 ? 'error' : 'success'}
          to="/risk"
        />
        <StatCard
          label="Open incidents"
          value={openIncidents}
          tone={openIncidents > 0 ? 'warning' : 'success'}
          to="/risk"
        />
        <StatCard label="DPIAs in review" value={dpiasInReview} to="/risk" />
      </div>
      <QuickLinks
        links={[
          { to: '/compliance', label: 'Compliance controls' },
          { to: '/risk', label: 'Risk & privacy registers' },
          { to: '/automation', label: 'Automation' },
          { to: '/fleet', label: 'Fleet & keys' },
          { to: '/audit', label: 'Audit log' },
        ]}
      />
    </div>
  );
}

/** Auditor evidence room — strictly read-only, export is the only verb. */
export function AuditorEvidenceRoom({ displayName }: { displayName: string }) {
  const toast = useToast();
  const [controls, setControls] = useState<ComplianceControl[]>([]);
  const [reports, setReports] = useState<ReportListItem[]>([]);

  useEffect(() => {
    void Promise.allSettled([
      fetchComplianceControls().then(setControls),
      fetchReports().then(setReports),
    ]);
  }, []);

  return (
    <div className="space-y-6" data-testid="home-auditor">
      <PageHeader
        title="Evidence Room"
        subtitle={`Welcome ${displayName}. Read-only by construction: every surface you can open here is a view, and the audit-ready export is the only action available to you.`}
        actions={
          <Button
            variant="secondary"
            onClick={() =>
              void downloadComplianceExport()
                .then(() => toast.success('Export downloaded (this access is itself audited)'))
                .catch((err: Error) => toast.error(err.message))
            }
          >
            Export evidence CSV
          </Button>
        }
      />
      <HomeSection title="Control posture (four independent flags)">
        <ul className="space-y-2">
          {controls.map((c) => (
            <li
              key={c.id}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2 text-sm"
            >
              <span className="font-mono text-xs text-text-muted">{c.controlKey}</span>
              <span className="min-w-0 flex-1 font-medium text-text-primary">{c.title}</span>
              <Badge tone={c.configured ? 'success' : 'neutral'}>configured</Badge>
              <Badge tone={c.evidenced ? 'success' : 'neutral'}>evidenced</Badge>
              <Badge tone={c.tested ? 'success' : 'neutral'}>tested</Badge>
              <Badge tone={c.interpreted ? 'success' : 'neutral'}>interpreted</Badge>
            </li>
          ))}
          {controls.length === 0 && (
            <li className="text-sm text-text-muted">No controls visible.</li>
          )}
        </ul>
      </HomeSection>
      <HomeSection title="Stored report snapshots">
        <ul className="space-y-1 text-sm">
          {reports.slice(0, 5).map((r) => (
            <li key={r.id}>
              <Link to={`/reports/${r.id}`} className="font-medium text-link hover:underline">
                {r.title}
              </Link>
            </li>
          ))}
          {reports.length === 0 && <li className="text-text-muted">No report snapshots yet.</li>}
        </ul>
      </HomeSection>
      <QuickLinks
        links={[
          { to: '/compliance', label: 'Control library' },
          { to: '/risk', label: 'Registers' },
          { to: '/audit', label: 'Audit log' },
          { to: '/reports', label: 'Reports' },
        ]}
      />
    </div>
  );
}
